import React, { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import { formatDate } from "../../../../../../formatDate";
import { Link, useParams, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Window from "../../../../../elements/window";

function ListGroup(props) {
  const params = useParams();
  const location = useLocation();
  const [table, setTable] = useState({
    head: [
      "Group",
      "No. of Brands",
      "No. of Outlets",
      "Date of Expiration",
      "Actions",
    ],
    data: false,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [load, setLoad] = useState(0); // 0: loading, 1: data loaded, 2: ready for export

  // Ref to track previous export state
  const prevExport = useRef(0);

  useEffect(() => {
    const fetchData = async () => {
      if (props.user && props.user.data) {
        setLoad(0); // Start loading
        try {
          const response = await axios.get(
            props.chimeraserv.urlGet + "/group",
            {
              headers: {
                Authorization: props.user.data.usrKey,
                "Content-Type": "application/json",
                Accept: "application/json",
              },
            }
          );
          let data = [];
          if (Object.keys(response.data).length) {
            data = response.data;
          }
          setTable((prev) => ({
            ...prev,
            data: data,
          }));
          setLoad(1); // Data loaded
        } catch (error) {
          setTable((prev) => ({
            ...prev,
            data: [],
          }));
          setLoad(1); // Even on error, mark as loaded
        }
      }
    };
    fetchData();
    // eslint-disable-next-line
  }, [props.user, props.chimeraserv.urlGet]);

  // Set load to 2 when data is ready
  useEffect(() => {
    if (load === 1 && table.data !== false) {
      setLoad(2);
    }
  }, [load, table.data]);

  // Filter, search, and sort logic using useMemo for performance
  const filteredAndSortedData = useMemo(() => {
    if (!table.data) return [];

    let filtered = table.data.filter((group) => {
      // Search filter
      const matchesSearch =
        searchTerm === "" ||
        group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        group.grpKey.toLowerCase().includes(searchTerm.toLowerCase());

      // Status filter
      const matchesStatus =
        filterStatus === "all" ||
        (filterStatus === "active" && group.status === 1) ||
        (filterStatus === "inactive" && group.status === 0);

      return matchesSearch && matchesStatus;
    });

    // Sort data
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case "name":
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case "brands":
          aValue = a.brands.length;
          bValue = b.brands.length;
          break;
        case "outlets":
          aValue = [].concat
            .apply(
              [],
              a.brands.map((brand) => brand.chains.map((chain) => chain).length)
            )
            .reduce((a, b) => a + b, 0);
          bValue = [].concat
            .apply(
              [],
              b.brands.map((brand) => brand.chains.map((chain) => chain).length)
            )
            .reduce((a, b) => a + b, 0);
          break;
        case "expiration":
          aValue = new Date(a.accntExp);
          bValue = new Date(b.accntExp);
          break;
        default:
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [table.data, searchTerm, filterStatus, sortBy, sortOrder]);

  // Component that receives props from Window component - handles export
  const GroupListContent = ({ export: exportTime, getData }) => {
    // Export logic
    useEffect(() => {
      if (prevExport.current !== exportTime && load === 2 && getData) {
        prevExport.current = exportTime;

        // Prepare export data based on filtered results
        const exportData = filteredAndSortedData.map((group, index) => {
          const outletsCount = [].concat
            .apply(
              [],
              group.brands.map(
                (brand) => brand.chains.map((chain) => chain).length
              )
            )
            .reduce((a, b) => a + b, 0);

          const status = group.status === 1 ? "Active" : "Inactive";

          return [
            (index + 1).toString(),
            group.name,
            group.brands.length.toString(),
            outletsCount.toString(),
            formatDate.longShort(group.accntExp),
            status,
          ];
        });

        // Get summary statistics
        const totalGroups = filteredAndSortedData.length;
        const activeGroups = filteredAndSortedData.filter(
          (g) => g.status === 1
        ).length;
        const inactiveGroups = filteredAndSortedData.filter(
          (g) => g.status === 0
        ).length;
        const totalBrands = filteredAndSortedData.reduce(
          (sum, g) => sum + g.brands.length,
          0
        );
        const totalOutlets = filteredAndSortedData.reduce((sum, g) => {
          return (
            sum +
            [].concat
              .apply(
                [],
                g.brands.map(
                  (brand) => brand.chains.map((chain) => chain).length
                )
              )
              .reduce((a, b) => a + b, 0)
          );
        }, 0);

        // Create summary data
        const summaryData = [
          ["Total Groups", totalGroups.toString()],
          ["Active Groups", activeGroups.toString()],
          ["Inactive Groups", inactiveGroups.toString()],
          ["Total Brands", totalBrands.toString()],
          ["Total Outlets", totalOutlets.toString()],
          ...(searchTerm || filterStatus !== "all"
            ? [
                ["Filtered Results", "Yes"],
                ...(searchTerm ? [["Search Term", searchTerm]] : []),
                ...(filterStatus !== "all"
                  ? [
                      [
                        "Status Filter",
                        filterStatus.charAt(0).toUpperCase() +
                          filterStatus.slice(1),
                      ],
                    ]
                  : []),
                ...(sortBy !== "name"
                  ? [
                      [
                        "Sorted By",
                        sortBy.charAt(0).toUpperCase() +
                          sortBy.slice(1) +
                          " (" +
                          sortOrder.toUpperCase() +
                          ")",
                      ],
                    ]
                  : []),
              ]
            : []),
        ];

        getData({
          type: "table",
          title: "Group Management Report",
          body: [
            // Main group data table
            {
              head: [
                [
                  "#",
                  "Group Name",
                  "No. of Brands",
                  "No. of Outlets",
                  "Expiration Date",
                  "Status",
                ],
              ],
              body: exportData,

              sheetName: "Groups",
            },
            // Summary table
            {
              head: [["Summary", "Count"]],
              body: summaryData,
              sheetName: "Summary",
            },
          ],
          id: "exportPdf_GroupList",
        });
      }
      // eslint-disable-next-line
    }, [
      exportTime,
      load,
      filteredAndSortedData,
      searchTerm,
      filterStatus,
      sortBy,
      sortOrder,
      getData,
    ]);

    return (
      <div className="table-container">
        <table className="table table-striped defaultTable">
          <thead>
            <tr>
              <th
                scope="col"
                onClick={() => handleSort("name")}
                className="sortable"
              >
                Group
                <FontAwesomeIcon
                  className="sort-icon"
                  icon={["fas", getSortIcon("name")]}
                />
              </th>
              <th
                scope="col"
                onClick={() => handleSort("brands")}
                className="sortable"
              >
                No. of Brands
                <FontAwesomeIcon
                  className="sort-icon"
                  icon={["fas", getSortIcon("brands")]}
                />
              </th>
              <th
                scope="col"
                onClick={() => handleSort("outlets")}
                className="sortable"
              >
                No. of Outlets
                <FontAwesomeIcon
                  className="sort-icon"
                  icon={["fas", getSortIcon("outlets")]}
                />
              </th>
              <th
                scope="col"
                onClick={() => handleSort("expiration")}
                className="sortable"
              >
                Date of Expiration
                <FontAwesomeIcon
                  className="sort-icon"
                  icon={["fas", getSortIcon("expiration")]}
                />
              </th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedData.length > 0 ? (
              renderData()
            ) : (
              <tr>
                <td
                  colSpan={table.head.length}
                  className="text-center no-results"
                >
                  {searchTerm || filterStatus !== "all" ? (
                    <div>
                      <FontAwesomeIcon
                        icon={["fas", "search"]}
                        className="no-results-icon"
                      />
                      <p>No groups found matching your search criteria</p>
                      <button className="btn btn-link" onClick={clearFilters}>
                        Clear filters to see all groups
                      </button>
                    </div>
                  ) : (
                    <div>
                      <FontAwesomeIcon
                        icon={["fas", "building"]}
                        className="no-results-icon"
                      />
                      <p>No groups available</p>
                    </div>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const valueChange = async (data, key) => {
    const updatedStatus = data.status === 1 ? 0 : 1;
    const updatedData = { ...data, status: updatedStatus };

    // Optimistically update UI
    setTable((prev) => ({
      ...prev,
      data: prev.data.map((item) =>
        item._id === data._id ? { ...item, status: updatedStatus } : item
      ),
    }));

    try {
      await axios.put(
        props.chimeraserv.urlPost + "/group/update",
        updatedData,
        {
          headers: {
            Authorization: props.user.data.usrKey,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );
    } catch (error) {
      // Revert UI change on error
      setTable((prev) => ({
        ...prev,
        data: prev.data.map((item) =>
          item._id === data._id ? { ...item, status: data.status } : item
        ),
      }));
      console.error("Error updating group status:", error);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleStatusFilter = (e) => {
    setFilterStatus(e.target.value);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setFilterStatus("all");
    setSortBy("name");
    setSortOrder("asc");
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return "sort";
    return sortOrder === "asc" ? "sort-up" : "sort-down";
  };

  const renderData = () =>
    filteredAndSortedData.map((data, key) => (
      <tr key={data._id}>
        <td>
          <div className="group-info">
            <strong>{data.name}</strong>
          </div>
        </td>
        <td>
          <span className="count-badge">{data.brands.length}</span>
        </td>
        <td>
          <span className="count-badge">
            {[].concat
              .apply(
                [],
                data.brands.map(
                  (brand) => brand.chains.map((chain) => chain).length
                )
              )
              .reduce((a, b) => a + b, 0)}
          </span>
        </td>
        <td>
          <div className="date-info">{formatDate.longShort(data.accntExp)}</div>
        </td>
        <td className="actions">
          {props.user.data.level === 0 ? (
            <div className="actions actionsAdmin">
              <label className="switch">
                <input
                  type="checkbox"
                  id={data.grpKey}
                  checked={!!data.status}
                  onChange={() => valueChange(data, key)}
                />
                <span className="slider"></span>
              </label>
              <Link
                to={
                  location.pathname.split("/").splice(0, 3).join("/") +
                  "/main/edit/" +
                  data._id
                }
              >
                <FontAwesomeIcon className="icon" icon={["fas", "cog"]} /> Edit
              </Link>
            </div>
          ) : (
            <div />
          )}
          <Link
            to={
              location.pathname.split("/").splice(0, 3).join("/") +
              "/main/view/" +
              data._id
            }
          >
            <FontAwesomeIcon className="icon" icon={["fas", "eye"]} /> View
          </Link>
        </td>
      </tr>
    ));

  const Loader =
    props.components && props.components.setting && props.components.setting[1]
      ? props.components.setting[1].component
      : () => <div>Loading...</div>;

  return (
    <div className="custForm" id="exportPdf_GroupList">
      <div className="viewTitle">List of Groups</div>

      {/* Search and Filter Controls */}
      <div className="table-controls">
        <div className="search-section">
          <div className="search-input-group">
            <FontAwesomeIcon className="search-icon" icon={["fas", "search"]} />
            <input
              type="text"
              className="form-control search-input"
              placeholder="Search by group name or key..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
            {searchTerm && (
              <button
                type="button"
                className="clear-search"
                onClick={() => setSearchTerm("")}
                title="Clear search"
              >
                <FontAwesomeIcon icon={["fas", "times"]} />
              </button>
            )}
          </div>
        </div>

        <div className="filters-section">
          <select
            className="form-control filter-select"
            value={filterStatus}
            onChange={handleStatusFilter}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <button
            type="button"
            className="btn btn-secondary clear-filters"
            onClick={clearFilters}
            title="Clear all filters"
          >
            <FontAwesomeIcon icon={["fas", "refresh"]} /> Clear
          </button>
        </div>
      </div>

      {/* Results Summary */}
      {table.data && (
        <div className="results-summary">
          Showing {filteredAndSortedData.length} of {table.data.length} groups
          {(searchTerm || filterStatus !== "all") && (
            <span className="filtered-indicator">
              <FontAwesomeIcon icon={["fas", "filter"]} /> Filtered
            </span>
          )}
        </div>
      )}

      <div>
        {!table.data ? (
          <Loader />
        ) : (
          <Window
            user={props.user}
            chimeraserv={props.chimeraserv}
            title={"Groups"}
          >
            <GroupListContent />
          </Window>
        )}
      </div>
    </div>
  );
}

export default ListGroup;
